function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { SseClient } from './SseClient';
// Browser-compatible EventEmitter implementation
let SimpleEventEmitter = class SimpleEventEmitter {
    on(event, listener) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(listener);
    }
    emit(event, ...args) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            eventListeners.forEach((listener)=>listener(...args));
        }
    }
    removeAllListeners() {
        this.listeners.clear();
    }
    constructor(){
        _define_property(this, "listeners", new Map());
    }
};
export class McpManager extends SimpleEventEmitter {
    // Server Management
    async addServer(config) {
        if (this.clients.has(config.id)) {
            throw new Error(`Server ${config.id} already exists`);
        }
        const client = this.createClient(config);
        this.setupClientListeners(client);
        this.clients.set(config.id, client);
        this.emit('serverAdded', config);
        this.log('info', config.id, `Server added: ${config.name}`);
    }
    async removeServer(serverId) {
        const client = this.clients.get(serverId);
        if (!client) {
            throw new Error(`Server ${serverId} not found`);
        }
        await client.disconnect();
        client.destroy();
        this.clients.delete(serverId);
        this.emit('serverRemoved', serverId);
        this.log('info', serverId, `Server removed: ${serverId}`);
    }
    async updateServer(serverId, config) {
        const existingClient = this.clients.get(serverId);
        if (!existingClient) {
            throw new Error(`Server ${serverId} not found`);
        }
        // Disconnect existing client
        await existingClient.disconnect();
        existingClient.destroy();
        // Create new client with updated config
        const newClient = this.createClient(config);
        this.setupClientListeners(newClient);
        this.clients.set(serverId, newClient);
        this.emit('serverStateChange', newClient.getState());
        this.log('info', serverId, `Server updated: ${config.name}`);
    }
    async connectServer(serverId) {
        const client = this.getClient(serverId);
        await client.connect();
    }
    async disconnectServer(serverId) {
        const client = this.getClient(serverId);
        await client.disconnect();
    }
    getServerState(serverId) {
        const client = this.getClient(serverId);
        return client.getState();
    }
    getAllServers() {
        return Array.from(this.clients.values()).map((client)=>client.getState());
    }
    // Tool Operations
    async listTools(serverId) {
        const client = this.getClient(serverId);
        return await client.listTools();
    }
    async executeTool(serverId, toolName, args) {
        const client = this.getClient(serverId);
        const execution = await client.executeTool(toolName, args);
        this.executions.set(execution.id, execution);
        this.emit('executionUpdate', execution);
        return execution;
    }
    // Execution Management
    getExecution(executionId) {
        return this.executions.get(executionId);
    }
    getAllExecutions() {
        return Array.from(this.executions.values());
    }
    getServerExecutions(serverId) {
        return Array.from(this.executions.values()).filter((exec)=>exec.serverId === serverId);
    }
    // Logging
    getLogs(serverId, limit) {
        let logs = this.logs;
        if (serverId) {
            logs = logs.filter((log)=>log.serverId === serverId);
        }
        if (limit) {
            logs = logs.slice(-limit);
        }
        return logs;
    }
    clearLogs(serverId) {
        if (serverId) {
            this.logs = this.logs.filter((log)=>log.serverId !== serverId);
        } else {
            this.logs = [];
        }
        this.emit('logsCleared', serverId);
    }
    // Private Methods
    createClient(config) {
        switch(config.transport){
            case 'sse':
                return new SseClient(config);
            // TODO: Add support for other client types
            // case 'websocket':
            //   return new WebSocketClient(config);
            // case 'stdio':
            //   return new StdioClient(config);
            default:
                throw new Error(`Unsupported server transport: ${config.transport}`);
        }
    }
    setupClientListeners(client) {
        const serverId = client.getState().config.id;
        client.on('statusChange', (state)=>{
            this.emit('serverStateChange', state);
        });
        client.on('event', (event)=>{
            this.emit('serverEvent', event);
        });
        client.on('executionStart', (execution)=>{
            this.executions.set(execution.id, execution);
            this.emit('executionStart', execution);
            this.log('info', serverId, `Execution started: ${execution.tool}`);
        });
        client.on('executionComplete', (execution)=>{
            this.executions.set(execution.id, execution);
            this.emit('executionComplete', execution);
            const status = execution.status === 'completed' ? 'completed' : 'failed';
            const message = execution.status === 'completed' ? `Execution completed: ${execution.tool}` : `Execution failed: ${execution.tool} - ${execution.error}`;
            this.log(execution.status === 'completed' ? 'info' : 'error', serverId, message);
        });
    }
    getClient(serverId) {
        const client = this.clients.get(serverId);
        if (!client) {
            throw new Error(`Server ${serverId} not found`);
        }
        return client;
    }
    log(level, serverId, message, data) {
        const entry = {
            id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
            serverId,
            level,
            message,
            data
        };
        this.logs.push(entry);
        // Trim logs if exceeding max entries
        if (this.logs.length > this.maxLogEntries) {
            this.logs = this.logs.slice(-this.maxLogEntries);
        }
        this.emit('log', entry);
    }
    // Cleanup
    async destroy() {
        // Disconnect and destroy all clients
        for (const [serverId, client] of this.clients){
            try {
                await client.disconnect();
                client.destroy();
            } catch (error) {
                console.error(`Error destroying client ${serverId}:`, error);
            }
        }
        this.clients.clear();
        this.executions.clear();
        this.logs = [];
        this.removeAllListeners();
    }
    constructor(){
        super(), _define_property(this, "clients", new Map()), _define_property(this, "executions", new Map()), _define_property(this, "logs", []), _define_property(this, "maxLogEntries", 1000);
    }
}
